import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Building2, Users, Inbox, TrendingUp, CheckCircle2, Clock, ArrowUpRight, Plus } from "lucide-react";
import { api, money } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, accent, testid }) => (
  <div className="border border-border bg-card p-6" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] tracking-[0.12em] uppercase text-muted-foreground">{label}</span>
      <Icon size={16} className={accent ? "text-onyx-wood" : "text-muted-foreground"} />
    </div>
    <div className="font-display font-extrabold text-3xl mt-4 tracking-tight">{value}</div>
  </div>
);

const STATUS = { draft: "bg-secondary text-foreground", sent: "bg-blue-100 text-blue-700", approved: "bg-green-100 text-green-700", rejected: "bg-red-100 text-red-700" };

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">Overview of your pipeline and activity.</p>
        </div>
        <Link to="/admin/proposals/new" data-testid="dashboard-new-proposal" className="flex items-center gap-2 bg-foreground text-background px-5 py-2.5 text-[12px] tracking-[0.1em] uppercase hover:bg-onyx-wood hover:text-onyx-black transition-colors"><Plus size={15} /> New Proposal</Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard icon={TrendingUp} label="Pipeline Value" value={money(s.pipeline_value)} accent testid="stat-pipeline" />
        <StatCard icon={CheckCircle2} label="Won Value" value={money(s.won_value)} testid="stat-won" />
        <StatCard icon={FileText} label="Total Proposals" value={s.total_proposals} testid="stat-total" />
        <StatCard icon={Clock} label="Pending" value={s.pending} testid="stat-pending" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard icon={CheckCircle2} label="Approved" value={s.approved} testid="stat-approved" />
        <StatCard icon={Building2} label="Active Projects" value={s.active_projects} testid="stat-projects" />
        <StatCard icon={Users} label="Customers" value={s.customers} testid="stat-customers" />
        <StatCard icon={Inbox} label="New Leads" value={s.leads} testid="stat-leads" />
      </div>

      <div className="border border-border bg-card">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-display font-bold text-lg tracking-tight">Recent Proposals</h2>
          <Link to="/admin/proposals" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">View all <ArrowUpRight size={14} /></Link>
        </div>
        <div className="divide-y divide-border">
          {(s.recent_proposals || []).map((p) => (
            <Link key={p.id} to={`/admin/proposals/${p.id}/edit`} className="flex items-center justify-between px-6 py-4 hover:bg-secondary/50 transition-colors" data-testid={`recent-proposal-${p.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{p.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.proposal_number} · {p.customer?.name || p.customer?.company || "—"}</div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <span className="font-mono-num text-sm">{money(p.total)}</span>
                <span className={`text-[10px] tracking-wide uppercase px-2.5 py-1 ${STATUS[p.status] || STATUS.draft}`}>{p.status}</span>
              </div>
            </Link>
          ))}
          {(!s.recent_proposals || s.recent_proposals.length === 0) && <div className="px-6 py-10 text-center text-muted-foreground text-sm">No proposals yet.</div>}
        </div>
      </div>
    </div>
  );
}
